import {
  doesFocusableExist,
  getCurrentFocusKey,
  managedFocusKeys,
  setFocus,
  setScreenFocusDefault,
} from '@akpl/ui-tv';
import { useEffect } from 'react';

import { analytics } from '../analytics';

/**
 * On mount: reports `screen_view`, tells the focus watchdog which screen this is and what its
 * default focusable is, and moves the focus there — unless focus already sits on a live element
 * outside the rail (e.g. a dialog restored it).
 *
 * Norigin keeps the key of an unmounted element as "current", so existence is always checked.
 * When the declared default is not mounted either — a Home whose first row is not the hero, a
 * Player opened straight into the drawer — the first focusable actually on screen takes it
 * (docs/04 §2.1); landing on nothing is what killed the remote in QA3 T1.
 */
export function useScreenFocus(screen: string, defaultFocusKey: string, enabled = true): void {
  // Registered even while disabled: the watchdog needs the screen's default from the first
  // render, which is exactly when the screen has no content to focus yet.
  useEffect(() => {
    setScreenFocusDefault(screen, defaultFocusKey);
  }, [screen, defaultFocusKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    analytics.screenView(screen);
    const id = window.setTimeout(() => {
      const current = getCurrentFocusKey();
      const live =
        current &&
        current !== 'SN:ROOT' &&
        !current.startsWith('rail') &&
        doesFocusableExist(current);
      if (live) return;
      if (doesFocusableExist(defaultFocusKey)) {
        void setFocus(defaultFocusKey);
        return;
      }
      const first = managedFocusKeys(document)[0];
      if (first && doesFocusableExist(first)) void setFocus(first);
    }, 0);
    return () => window.clearTimeout(id);
  }, [screen, defaultFocusKey, enabled]);
}
